import { getNamespacesChains, getNamespacesMethodsForChainId, getNamespacesEventsForChainId, getAccountsChains, } from "./namespaces";
import { hasOverlap } from "./misc";
export function isSessionCompatible(session, params) {
    const { requiredNamespaces } = params;
    const sessionKeys = Object.keys(session.namespaces);
    const paramsKeys = Object.keys(requiredNamespaces);
    let compatible = true;
    if (!hasOverlap(paramsKeys, sessionKeys))
        return false;
    sessionKeys.forEach(key => {
        const { accounts, methods, events, extension } = session.namespaces[key];
        const chains = getAccountsChains(accounts);
        const requiredNamespace = requiredNamespaces[key];
        if (!hasOverlap(requiredNamespace.chains, chains) ||
            !hasOverlap(requiredNamespace.methods, methods) ||
            !hasOverlap(requiredNamespace.events, events)) {
            compatible = false;
        }
        if (compatible && extension) {
            extension.forEach(extensionNamespace => {
                var _a;
                const { accounts, methods, events } = extensionNamespace;
                const chains = getAccountsChains(accounts);
                const overlap = (_a = requiredNamespace.extension) === null || _a === void 0 ? void 0 : _a.find(ext => hasOverlap(ext.chains, chains) &&
                    hasOverlap(ext.methods, methods) &&
                    hasOverlap(ext.events, events));
                if (!overlap)
                    compatible = false;
            });
        }
    });
    return compatible;
}
export function isValidArray(arr, itemCondition) {
    if (Array.isArray(arr)) {
        if (typeof itemCondition !== "undefined" && arr.length) {
            const matches = arr.filter(itemCondition);
            return matches.length === arr.length;
        }
        else {
            return true;
        }
    }
    return false;
}
export function isValidObject(obj) {
    return Object.getPrototypeOf(obj) === Object.prototype && Object.keys(obj).length;
}
export function isUndefined(input) {
    return typeof input === "undefined";
}
export function isValidString(input, optional) {
    if (optional && isUndefined(input))
        return true;
    return typeof input === "string" && Boolean(input.trim().length);
}
export function isValidNumber(input, optional) {
    if (optional && isUndefined(input))
        return true;
    return typeof input === "number";
}
export function isValidChainId(value, optional) {
    if (typeof value === "undefined" && optional)
        return true;
    if (isValidString(value, false) && value.includes(":")) {
        const split = value.split(":");
        return split.length === 2;
    }
    return false;
}
export function isValidAccountId(value) {
    if (isValidString(value, false) && value.includes(":")) {
        const split = value.split(":");
        if (split.length === 3) {
            const chainId = split[0] + ":" + split[1];
            return !!split[2] && isValidChainId(chainId, false);
        }
    }
    return false;
}
export function isValidUrl(value) {
    if (isValidString(value, false)) {
        try {
            const url = new URL(value);
            return typeof url !== "undefined";
        }
        catch (e) {
            return false;
        }
    }
    return false;
}
export function isProposalStruct(input) {
    var _a;
    return (_a = input === null || input === void 0 ? void 0 : input.proposer) === null || _a === void 0 ? void 0 : _a.publicKey;
}
export function isSessionStruct(input) {
    return input === null || input === void 0 ? void 0 : input.topic;
}
export function isValidRequiredNamespaceBody(input) {
    const { methods, events, chains } = input;
    let validChains = true;
    const validRequiredNamespace = isValidArray(methods) && isValidArray(events) && isValidArray(chains);
    if (validRequiredNamespace) {
        chains.forEach((chain) => {
            if (!isValidChainId(chain, false))
                validChains = false;
        });
    }
    return validRequiredNamespace && validChains;
}
export function isValidRequiredNamespaces(input, optional) {
    let valid = false;
    if (optional && !input)
        return true;
    else if (input && isValidObject(input)) {
        valid = true;
        Object.values(input).forEach((namespace) => {
            if (!isValidRequiredNamespaceBody(namespace))
                valid = false;
            if (valid && (namespace === null || namespace === void 0 ? void 0 : namespace.extension)) {
                if (!isValidArray(namespace.extension))
                    valid = false;
                namespace.extension.forEach((extension) => {
                    if (!isValidRequiredNamespaceBody(extension))
                        valid = false;
                });
            }
        });
    }
    return valid;
}
export function isValidNamespaceBody(input) {
    const { methods, events, accounts } = input;
    let validAccounts = true;
    const validNamespace = isValidArray(methods) && isValidArray(events) && isValidArray(accounts);
    if (validNamespace) {
        accounts.forEach((account) => {
            if (!isValidAccountId(account))
                validAccounts = false;
        });
    }
    return validNamespace && validAccounts;
}
export function isValidNamespaces(input, optional) {
    let valid = false;
    if (optional && !input)
        return true;
    else if (input && isValidObject(input)) {
        valid = true;
        Object.values(input).forEach((namespace) => {
            if (!isValidNamespaceBody(namespace))
                valid = false;
            if (valid && (namespace === null || namespace === void 0 ? void 0 : namespace.extension)) {
                if (!isValidArray(namespace.extension))
                    valid = false;
                namespace.extension.forEach((extension) => {
                    if (!isValidNamespaceBody(extension))
                        valid = false;
                });
            }
        });
    }
    return valid;
}
export function isValidRelay(input) {
    return isValidString(input.protocol, true);
}
export function isValidRelays(input, optional) {
    let valid = false;
    if (optional && !input)
        valid = true;
    else if (input && isValidArray(input) && input.length) {
        input.forEach((relay) => {
            valid = isValidRelay(relay);
        });
    }
    return valid;
}
export function isValidId(input) {
    return typeof input === "number";
}
export function isValidParams(input) {
    return typeof input !== "undefined" && typeof input !== null;
}
export function isValidAccounts(input, optional) {
    let valid = false;
    if (optional && !input)
        valid = true;
    else if (input && isValidArray(input) && input.length) {
        input.forEach((account) => {
            valid = isValidAccountId(account);
        });
    }
    return valid;
}
export function isValidErrorReason(input) {
    if (!input)
        return false;
    if (typeof input !== "object")
        return false;
    if (!input.code || !isValidNumber(input.code, false))
        return false;
    if (!input.message || !isValidString(input.message, false))
        return false;
    return true;
}
export function isValidRequest(request) {
    if (isUndefined(request))
        return false;
    if (!isValidString(request.method, false))
        return false;
    return true;
}
export function isValidResponse(response) {
    if (isUndefined(response))
        return false;
    if (isUndefined(response.result) && isUndefined(response.error))
        return false;
    if (!isValidNumber(response.id, false))
        return false;
    if (!isValidString(response.jsonrpc, false))
        return false;
    return true;
}
export function isValidEvent(event) {
    if (isUndefined(event))
        return false;
    if (!isValidString(event.name, false))
        return false;
    return true;
}
export function isValidNamespacesChainId(namespaces, chainId) {
    if (!isValidChainId(chainId, false))
        return false;
    const chains = getNamespacesChains(namespaces);
    if (!chains.includes(chainId))
        return false;
    return true;
}
export function isValidNamespacesRequest(namespaces, chainId, method) {
    if (!isValidString(method, false))
        return false;
    const methods = getNamespacesMethodsForChainId(namespaces, chainId);
    return methods.includes(method);
}
export function isValidNamespacesEvent(namespaces, chainId, eventName) {
    if (!isValidString(eventName, false))
        return false;
    const events = getNamespacesEventsForChainId(namespaces, chainId);
    return events.includes(eventName);
}
//# sourceMappingURL=validators.js.map